import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { startExploreUnknownBotMission } from './utils/startExploreUnknownBotMission';

/**
 * Transient route between a boss tactical loss and the next battle session.
 * Fully unmounts BattleUI so mulligan and WebSocket start on a clean instance.
 */
export default function ExploreUnknownBossInstantRetry() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  const missionIndex = location.state?.exploreUnknownBossRetryMissionIndex;
  const runId = location.state?.exploreUnknownBossRetryRunId;
  const strategyId = location.state?.exploreUnknownBossRetryStrategyId;
  const retryAttempt = location.state?.exploreUnknownBossRetryAttempt;
  const devDirectBossMission = location.state?.exploreUnknownDevDirectBossMission === true;

  useEffect(() => {
    if (missionIndex == null) {
      throw new Error('ExploreUnknownBossInstantRetry: exploreUnknownBossRetryMissionIndex is required');
    }
    if (runId == null) {
      throw new Error('ExploreUnknownBossInstantRetry: exploreUnknownBossRetryRunId is required');
    }
    if (strategyId == null || strategyId === '') {
      throw new Error('ExploreUnknownBossInstantRetry: exploreUnknownBossRetryStrategyId is required');
    }
    if (retryAttempt == null) {
      throw new Error('ExploreUnknownBossInstantRetry: exploreUnknownBossRetryAttempt is required');
    }
    const attemptNum = Number(retryAttempt);
    if (!Number.isInteger(attemptNum) || attemptNum < 2) {
      throw new Error(
        `ExploreUnknownBossInstantRetry: exploreUnknownBossRetryAttempt must be an integer >= 2 (${String(retryAttempt)})`
      );
    }

    let cancelled = false;
    const retryDarknessMeter = location.state?.exploreUnknownBossRetryDarknessMeter;
    const startOptions = { exploreUnknownBossRetryAttempt: attemptNum };
    if (retryDarknessMeter != null) {
      startOptions.exploreUnknownBossRetryDarknessMeter = retryDarknessMeter;
    }
    if (devDirectBossMission) {
      startOptions.exploreUnknownDevDirectBossMission = true;
    }
    void startExploreUnknownBotMission({
      missionIndex: Number(missionIndex),
      runId: Number(runId),
      strategyId: String(strategyId),
      navigate,
      options: startOptions,
    }).catch((err) => {
      if (cancelled) return;
      console.error('ExploreUnknownBossInstantRetry: failed to start boss retry battle', err);
      setError(err instanceof Error ? err.message : String(err));
    });

    return () => {
      cancelled = true;
    };
  }, [missionIndex, runId, strategyId, retryAttempt, devDirectBossMission, navigate]);

  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        color: '#e5e7eb',
        zIndex: 10000002,
      }}
    >
      <p style={{ margin: 0, fontSize: 18, letterSpacing: '0.08em' }}>
        {t('exploreUnknownPage.startingMission')}
      </p>
      {error ? (
        <p style={{ color: '#fca5a5', marginTop: 24, textAlign: 'center', maxWidth: 480, padding: '0 16px' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
